import { useLayoutEffect, useRef, useState, type RefObject } from 'react'

export interface ElementSize {
  width: number
  height: number
}

/**
 * Tracks an element's content box via ResizeObserver. Size is 0×0 until the
 * element mounts, so consumers should gate rendering on width/height > 0.
 */
export function useElementSize<T extends HTMLElement = HTMLDivElement>(): [RefObject<T>, ElementSize] {
  const ref = useRef<T>(null)
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 })

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const update = () => {
      const next = { width: Math.floor(el.clientWidth), height: Math.floor(el.clientHeight) }
      setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next))
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return [ref, size]
}
